import { supabase } from '../lib/supabase';

import { buildJobSearchCredentials } from './externalJobCredentials';

export interface RefreshJobFeedsParams {
  keywords: string;
  location?: string;
  maxResults?: number;
  experienceYears?: number | null;
  profileSkills?: string[];
}

export interface RefreshJobFeedsResult {
  ok: boolean;
  total: number;
  message: string;
  insufficient_credits?: boolean;
  credits_balance?: number;
  credits_deducted?: number;
  fetch_batch_id?: string;
  new_jobs_count?: number;
  batch_jobs_count?: number;
  ingest_since?: string;
  ingest_until?: string;
  peerlist?: { ok: boolean; created: number; updated: number; seen: number; error?: string | null };
  external?: {
    ok: boolean;
    total: number;
    sources: string[];
    source_info?: Record<string, { found: number; has_credentials: boolean }>;
    error?: string | null;
  };
  match_refresh?: boolean;
}

/** Refresh can take 60–120s; the default Supabase client fetch times out at 10s. */
const REFRESH_FETCH_TIMEOUT_MS = 120_000;

function resolveFunctionsBaseUrl(): string {
  const project =
    import.meta.env.VITE_SUPABASE_PROJECT_URL?.trim() ||
    import.meta.env.VITE_SUPABASE_URL?.trim() ||
    '';
  return project.replace(/\/$/, '');
}

/** Client transport/timeout errors — refresh may still be running server-side. */
export function isRefreshTransportError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('failed to send') ||
    m.includes('edge function') ||
    m.includes('timed out') ||
    m.includes('timeout') ||
    m.includes('abort') ||
    m.includes('network error') ||
    m.includes('fetch failed') ||
    m.includes('cors') ||
    m.includes('access-control')
  );
}

/** Never surface raw edge-function transport errors in the UI. */
export function userFacingRefreshMessage(message: string, ok: boolean): string | null {
  if (!message || isRefreshTransportError(message)) {
    return ok ? null : null;
  }
  if (message.toLowerCase().includes('empty response')) return null;
  return message;
}

/** Fetch fresh jobs into the database. */
export async function refreshJobFeeds(
  params: RefreshJobFeedsParams,
): Promise<RefreshJobFeedsResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    return { ok: false, total: 0, message: 'Sign in to refresh job feeds.' };
  }

  let linkedInExp: string[] | undefined;
  const expYears = params.experienceYears;
  if (expYears != null) {
    if (expYears <= 1) linkedInExp = ['internship', 'entry'];
    else if (expYears <= 3) linkedInExp = ['entry', 'associate'];
    else if (expYears <= 6) linkedInExp = ['associate', 'mid-senior'];
    else linkedInExp = ['mid-senior', 'director'];
  }

  const credentials = buildJobSearchCredentials();

  const body = {
    keywords: params.keywords,
    location: params.location ?? 'India',
    max_results: params.maxResults ?? 50,
    credentials,
    linkedin: {
      date_posted: 'past-month',
      sort_by: 'relevance',
      ...(linkedInExp ? { experience_level: linkedInExp } : {}),
    },
    naukri: {
      freshness: 15,
      key_skills: (params.profileSkills ?? []).slice(0, 8),
      ...(expYears != null
        ? {
            experience_min: Math.max(0, expYears - 1),
            experience_max: expYears + 2,
          }
        : {}),
    },
  };

  const baseUrl = resolveFunctionsBaseUrl();
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';
  const url = `${baseUrl}/functions/v1/refresh-job-feeds`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REFRESH_FETCH_TIMEOUT_MS);

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: anonKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    const data = (await res.json().catch(() => null)) as (RefreshJobFeedsResult & {
      error?: string;
      balance?: number;
      required?: number;
    }) | null;

    if (res.status === 402) {
      return {
        ok: false,
        total: 0,
        insufficient_credits: true,
        credits_balance: typeof data?.balance === 'number' ? data.balance : undefined,
        message: 'Insufficient credits to refresh job feeds.',
      };
    }

    if (!res.ok || !data) {
      const errMsg = data?.error ?? data?.message;
      return {
        ok: false,
        total: 0,
        message: typeof errMsg === 'string' && errMsg.length > 0 ? errMsg : 'Refresh failed — try again in a moment.',
      };
    }

    return {
      ...data,
      message: userFacingRefreshMessage(data.message ?? '', data.ok) ?? data.message ?? '',
    };
  } catch (e) {
    const msg = (e as Error).message ?? '';
    if (isRefreshTransportError(msg)) {
      // Server may still be ingesting — treat as background refresh, no error toast.
      return { ok: true, total: 0, message: '' };
    }
    return { ok: false, total: 0, message: 'Refresh failed — try again in a moment.' };
  } finally {
    clearTimeout(timer);
  }
}
